import React, { useEffect, useState } from "react"
import Page from "../../common/page/Page"
import axios from "axios"
import ButtonArea from "../../common/ButtonArea/ButtonArea"
import Form from "../../common/form/Form"
import ResponsiveTable from "../../common/ResponsiveTable/ResponsiveTable"
import EditIcon from "../../icons/EditIocn"
import DoubleArrowIcon from "../../icons/DoubleArrowIcon"
import Loader from "../../common/loader/Loader"
import XIcon from "../../icons/XIcon"
import TikIcon from "../../icons/TikIcon"
import { useFlashMessage } from "../../common/hook/useFlashMessage"
import FlashMessage from "../../common/FlashMesage/FlashMessage"

const ProductPage = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [formActive, setFormActive] = useState(false)
  const [isAdd, setIsAdd] = useState(true)
  const [category, setCategory] = useState([])
  const [currentProductId, setCurrentProductId] = useState(null)
  const [stockEditId, setStockEditId] = useState(null)
  const [stockValue, setStockValue] = useState(0)
  const { flash, showMessage, hideMessage } = useFlashMessage()
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    user_manual: "",
    stock: 0,
    rate: 0,
    weight: "",
    category_id: "",
    images: [],
  })

  const fetchProducts = async () => {
    try {
      const response = await axios.get("/api/products")
      setProducts(response.data.products || [])
    } catch (err) {
      setError(err.response?.data?.message || err.message)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      await fetchProducts()
      const categoriesResponse = await axios.get("/api/categories")
      setCategory(categoriesResponse.data || [])
      setLoading(false)
    }
    fetchData()
  }, [])

  const handleInputChange = (e) => {
    const { id, value } = e.target
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    const totalImages = formData.images.length + files.length
    if (totalImages > 5) {
      showMessage({
        message: `"Maximum 5 images allowed.`,
        type: "error",
        mode: "auto",
      })
      // alert("Maximum 5 images allowed.")
      return
    }
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }))
  }

  const removeImage = (index) => {
    setFormData((prev) => {
      const updatedImages = [...prev.images]
      updatedImages.splice(index, 1)
      return { ...prev, images: updatedImages }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim()) return alert("Product name is required.")
    if (!formData.description.trim()) return alert("Description is required.")
    if (!formData.user_manual.trim()) return alert("User manual is required.")
    if (formData.rate === "" || isNaN(formData.rate))
      return showMessage({
        message: `Rate must be a number.`,
        type: "error",
        mode: "auto",
      })
    if (!formData.category_id)
      return showMessage({
        message: `Category is required.`,
        type: "error",
        mode: "auto",
      })
    if (
      formData.weight !== "" &&
      (isNaN(formData.weight) || Number(formData.weight) < 0)
    )
      return showMessage({
        message: "Weight must be a non-negative number.",
        type: "error",
        mode: "auto",
      })
    if (isAdd && formData.images.length === 0) {
      return showMessage({
        message: "At least 1 image is required.",
        type: "error",
        mode: "auto",
      })
    }

    try {
      if (isAdd) {
        setLoading(true)
        setFormActive(false)
        const form = new FormData()
        form.append("name", formData.name)
        form.append("description", formData.description)
        form.append("user_manual", formData.user_manual)
        form.append("stock", formData.stock)
        form.append("rate", formData.rate)
        form.append("category_id", formData.category_id)
        form.append("weight", formData.weight)
        formData.images.forEach((file) => form.append("images", file))

        await axios.post("/api/products/create-product", form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        showMessage({
          message: "Product added successfully",
          type: "success",
          mode: "auto",
        })
      } else {
        const updatePayload = {
          name: formData.name,
          description: formData.description,
          user_manual: formData.user_manual,
          rate: formData.rate,
          category_id: formData.category_id,
          weight: formData.weight,
        }
        await axios.put(`/api/products/${currentProductId}`, updatePayload)

        showMessage({
          message: "Product updated successfully",
          type: "success",
          mode: "auto",
        })
      }

      await fetchProducts()

      setFormActive(false)
      setFormData({
        name: "",
        description: "",
        user_manual: "",
        stock: 0,
        rate: 0,
        weight: "",
        category_id: "",
        images: [],
      })
      setCurrentProductId(null)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      setFormActive(true)
      setError(err.response?.data?.error || err.message)
    }
  }

  const handleStockUpdate = async (productId) => {
    try {
      await axios.put(`/api/products/${productId}/stock`, { stock: stockValue })
      showMessage({
        message: "Stock updated successfully",
        type: "success",
        mode: "auto",
      })
      await fetchProducts()
      setStockEditId(null)
      setStockValue(0)
    } catch (err) {
      alert(err.response?.data?.error || err.message)
    }
  }

  return (
    <Page title={"Product"} is_admin={true}>
      {!formActive && (
        <ButtonArea>
          <button
            onClick={() => {
              setFormActive(true)
              setIsAdd(true)
            }}
          >
            Add Product
          </button>
        </ButtonArea>
      )}

      {formActive && (
        <Form is_admin={true} onSubmit={handleSubmit}>
          <div>
            <div>
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div>
            <div>
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div>
            <div>
              <label htmlFor="user_manual">User Manual *</label>
              <textarea
                id="user_manual"
                value={formData.user_manual}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div>
            <div>
              <label htmlFor="rate">Rate</label>
              <input
                type="number"
                id="rate"
                value={formData.rate}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div>
            <div>
              <label htmlFor="category_id">Category</label>
              <select
                id="category_id"
                value={formData.category_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Category</option>
                {category.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div>
              <label htmlFor="weight">Weight g</label>
              <input
                type="number"
                id="weight"
                value={formData.weight}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {isAdd && (
            <div>
              <div>
                <label htmlFor="stock">Stock</label>
                <input
                  type="number"
                  id="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          )}

          {isAdd && (
            <div>
              <div>
                <label htmlFor="images">Images (max 5)</label>
                <input
                  type="file"
                  id="images"
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </div>
            </div>
          )}

          {isAdd && formData.images.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {formData.images.map((file, index) => (
                <div key={index} style={{ position: "relative" }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    style={{
                      position: "absolute",
                      top: "0px",
                      right: "0px",
                      background: "red",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <ButtonArea>
            <button type="submit">{isAdd ? "Add" : "Update"} Product</button>
            <button
              type="button"
              onClick={() => {
                setFormActive(false)
                setFormData({
                  name: "",
                  description: "",
                  user_manual: "",
                  stock: 0,
                  rate: 0,
                  weight: "",
                  category_id: "",
                  images: [],
                })
                setCurrentProductId(null)
              }}
            >
              Close
            </button>
          </ButtonArea>
        </Form>
      )}

      {loading && (
        <div style={{ height: "calc( 100svh - 100px ) ", display: "flex" }}>
          <div style={{ margin: "auto" }}>
            <Loader />
          </div>
        </div>
      )}

      {!loading && !error && !formActive && (
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Id</th>
              <th>Image</th>
              <th>Name</th>
              <th>Stock</th>
              <th>Category</th>
              <th>Rate</th>
              <th>Weight</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id}>
                <td data-label="Id">{product.id}</td>
                <td data-label="Image">
                  {product.product_images &&
                    product.product_images.length > 0 && (
                      <img
                        src={`${
                          import.meta.env.VITE_BACKEND_URL
                        }/public/images/products/${
                          product.product_images[0].image_url
                        }`}
                        alt="Product"
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "10px",
                        }}
                      />
                    )}
                </td>
                <td data-label="Name">{product.name}</td>

                <td data-label="Stock">
                  {stockEditId === product.id ? (
                    <>
                      <input
                        type="number"
                        value={stockValue}
                        onChange={(e) => setStockValue(e.target.value)}
                        style={{ width: "60px", marginRight: "5px" }}
                      />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-around",
                        }}
                      >
                        <TikIcon
                          onClick={() => handleStockUpdate(product.id)}
                        />

                        <XIcon onClick={() => setStockEditId(null)} />
                      </div>
                    </>
                  ) : (
                    <>
                      <span
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          width: "fit-content",
                          justifyContent: "end",
                        }}
                      >
                        {product.stock}
                        <div style={{ marginLeft: "5px" }}>
                          <EditIcon
                            onClick={() => {
                              setStockEditId(product.id)
                              setStockValue(product.stock)
                            }}
                            // style={{ marginLeft: "5px" }}
                          />
                        </div>
                      </span>
                    </>
                  )}
                </td>

                <td data-label="Category">
                  {product.product_category
                    ? product.product_category.name
                    : "N/A"}
                </td>
                <td data-label="Rate">{product.rate}</td>
                <td data-label="Weight">{product.weight}</td>
                <td data-label="Actions">
                  <EditIcon
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setIsAdd(false)
                      setFormActive(true)
                      setCurrentProductId(product.id)
                      setFormData({
                        name: product.name,
                        description: product.description,
                        user_manual: product.user_manual,
                        stock: product.stock,
                        rate: product.rate,
                        weight: product.weight || "",
                        category_id: product.category_id,
                        images: [],
                      })
                    }}
                  />
                  <DoubleArrowIcon />
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
      {flash && (
        <FlashMessage
          message={flash.message}
          type={flash.type}
          mode={flash.mode}
          onClose={hideMessage}
        />
      )}
    </Page>
  )
}

export default ProductPage
